(() => {
  // -- Section: Type definitions & Inputs
  /**
   * @typedef {Object} CarouselInstance
   * @property {Function} destroy
   * @property {Function} slideTo
   * @property {Function} update
   */

  // -- Section: Constants
  const MOBILE_BREAKPOINT_QUERY = '(max-width: 749px)';
  const RETRY_DELAY_MS = 120;
  const SWIPER_SPEED_MS = 450;

  // -- Section: Component Definition
  class AspadzAffCards extends HTMLElement {
    constructor() {
      super();

      // -- Section: State & variables
      /** @type {CarouselInstance|null} */
      this.swiper = null;
      /** @type {number|null} */
      this.retryTimer = null;
      /** @type {MediaQueryList} */
      this.mobileQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
      /** @type {AbortController|null} */
      this.abortController = null;

      // Bind methods
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    // -- Section: Lifecycle hooks
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.addQueryListener(this.mobileQuery, this.handleViewportChange);
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });

      this.init();
    }

    disconnectedCallback() {
      window.clearTimeout(this.retryTimer);
      this.destroySwiper();

      this.abortController?.abort();
      this.abortController = null;

      this.removeQueryListener(this.mobileQuery, this.handleViewportChange);
    }

    // -- Section: Functions
    addQueryListener(query, callback) {
      if (query.addEventListener) {
        query.addEventListener('change', callback);
      } else {
        query.addListener(callback);
      }
    }

    removeQueryListener(query, callback) {
      if (query.removeEventListener) {
        query.removeEventListener('change', callback);
      } else {
        query.removeListener(callback);
      }
    }

    init() {
      // Only initialize mobile swiper when matching mobile query
      if (!this.mobileQuery.matches) {
        this.destroySwiper();
        return;
      }

      if (this.swiper) return;

      if (typeof window.AspadzConceptCarousel === 'undefined') {
        this.retryTimer = window.setTimeout(() => this.init(), RETRY_DELAY_MS);
        return;
      }

      const slider = this.querySelector('[data-aspadz-aff-cards-slider]');
      const slides = this.querySelectorAll('.aspadz-aff-cards__item');
      if (!slider || slides.length < 2) return;

      const paginationEl = this.querySelector('[data-aspadz-aff-cards-pagination]');
      const nextBtn = this.querySelector('[data-aspadz-aff-cards-next]');
      const prevBtn = this.querySelector('[data-aspadz-aff-cards-prev]');

      this.swiper = new window.AspadzConceptCarousel(slider, {
        allowTouchMove: true,
        autoplay: false,
        centeredSlides: false,
        initialSlide: 0,
        loop: false,
        navigation: {
          nextEl: nextBtn,
          prevEl: prevBtn,
        },
        pagination: {
          clickable: true,
          el: paginationEl,
          renderBullet(index, className) {
            return `<button class="${className}" type="button" aria-label="Go to slide ${index + 1}"></button>`;
          },
        },
        slidesPerView: 'auto',
        spaceBetween: 15,
        speed: SWIPER_SPEED_MS,
        watchOverflow: true,
      });
    }

    destroySwiper() {
      if (!this.swiper) return;

      try {
        this.swiper.destroy(true, true);
      } catch (e) {
        // Safe fallback
      }
      this.swiper = null;
    }

    // -- Section: Listeners
    handleViewportChange() {
      if (this.mobileQuery.matches) {
        this.init();
      } else {
        this.destroySwiper();
      }
    }

    handleBlockSelect(event) {
      if (!this.swiper || !event.target) return;
      const targetSlide = event.target.closest('.aspadz-aff-cards__item');
      if (!targetSlide) return;

      const slides = Array.from(this.querySelectorAll('.aspadz-aff-cards__item'));
      const index = slides.indexOf(targetSlide);
      if (index >= 0 && typeof this.swiper.slideTo === 'function') {
        this.swiper.slideTo(index);
      }
    }
  }

  if (!customElements.get('aspadz-aff-cards')) {
    customElements.define('aspadz-aff-cards', AspadzAffCards);
  }
})();
